import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getInitials, getName } from '../../utils/userUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Settings, LogOut, ChevronDown, LayoutDashboard, UserCog, UserRound, Inbox } from 'lucide-react';
import AppContext from '../../contexts/AppContext';
import { hasUserPermission, PERMISSIONS } from '../../utils/permissionUtils';
import { hasRole, ROLES } from '../../utils/roleUtils';
import { pendingBadge, pendingBadgeLabel, useMyInterventionRequests } from '../../hooks/useMyInterventionRequests';
import LanguageSwitcher from '../LanguageSwitcher';
import ImpersonationDialog from '../ImpersonationDialog';
import ImpersonationBanner from '../ImpersonationBanner';

const Header = ({ username, logo, serviceDeskName, onLogout }) => {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [impersonationOpen, setImpersonationOpen] = useState(false);
  const navigate = useNavigate();
  const { currentUser } = useContext(AppContext);
  const hasDashboardAccess = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_ACCESS);
  const canImpersonate = hasRole(currentUser?.role, ROLES.SUPERADMIN) && !currentUser?.impersonatedBy;
  // Strict equality, and deliberately so: `hasRole` without a modifier compares the two roles, so
  // an admin does not see this entry. Never write `'technician+'` here — the inclusive form would
  // put a technician-only screen in every admin's menu.
  const isTechnician = hasRole(currentUser?.role, ROLES.TECHNICIAN);
  /**
   * How many requests are still waiting on him. Same query key as the `/issues` segment, so the
   * header costs no extra request — and `enabled` keeps the four other roles from asking at all.
   * A count of state; amber while a reminder is unseen (E15), which the server's `seen_at` clears.
   */
  const { data: myRequests } = useMyInterventionRequests(isTechnician);
  const badge = pendingBadge(myRequests);
  const pendingRequests = badge.pending;
  const badgeTone = badge.unseenReminders > 0
    ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
    : 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300';

  const handleImpersonateClick = () => {
    setDropdownOpen(false);
    setImpersonationOpen(true);
  };

  const handleAdminClick = () => {
    setDropdownOpen(false);
    navigate('/admin');
  };

  const handleDashboardClick = () => {
    setDropdownOpen(false);
    navigate('/admin');
  };

  const handleProfileClick = () => {
    setDropdownOpen(false);
    navigate('/profile');
  };

  const handleMyRequestsClick = () => {
    setDropdownOpen(false);
    navigate('/issues?tab=requests');
  };

  const handleLogout = () => {
    setDropdownOpen(false);
    onLogout();
  };

  return (
    <nav className="bg-black h-[80px] flex items-center">
      <div className="w-full max-w-screen-md mx-auto px-4 h-full flex items-center justify-between">
          <div className="flex items-center gap-2 rounded-full">
          {logo && (
            <Link to="/home">
              <img src={logo} alt={serviceDeskName} className="w-[120px] h-auto" />
            </Link>
          )}
          {!logo && (
            <Link to="/home">
              <img src="/logo_gps_white.png" alt={'Globe Perfect Store'} className="w-[120px] h-auto" />
            </Link>
          )}
          </div>
        <div className="flex justify-between items-center gap-4">
          <ImpersonationBanner />
          <LanguageSwitcher />
          {username && (
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <button className="flex flex-row gap-1 items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none">
                  <span className="text-gray-400 text-base text-[14px] font-medium">
                    {getName(username)}
                  </span>
                  <div className="relative pb-1 rounded-full w-8 h-8">
                    <div className="w-8 h-8 rounded-full bg-[#CDC4BA] flex items-center justify-center">
                      <span className="text-white text-xs font-medium">
                        {getInitials(username)}
                      </span>
                    </div>
                    {/* Anchored to the avatar rather than to the row: the name beside it is hidden
                        on a narrow header, and a badge floating next to nothing reads as a glitch. */}
                    {pendingRequests > 0 && (
                      <span
                        className={`absolute -right-1 -top-1 flex h-[17px] min-w-[17px] items-center justify-center rounded-md border border-black px-1 text-[11px] font-semibold dark:border-zinc-900 ${badgeTone}`}
                      >
                        <span aria-hidden="true">{pendingRequests}</span>
                        <span className="sr-only">{pendingBadgeLabel(t, badge)}</span>
                      </span>
                    )}
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{getName(username)}</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {currentUser?.email || username}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {hasDashboardAccess && (
                  <>
                    <DropdownMenuItem onClick={handleDashboardClick}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      <span>{t('Admin dashboard')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {canImpersonate && (
                  <>
                    <DropdownMenuItem onClick={handleImpersonateClick}>
                      <UserCog className="mr-2 h-4 w-4" />
                      <span>{t('Impersonate a user')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {/* Where a technician looks for his own screen: the menu he already opens, and
                    not a third navigation affordance floating over the page. */}
                {isTechnician && (
                  <>
                    {/* His requests before his profile: it is the one entry he opens to act. */}
                    <DropdownMenuItem onClick={handleMyRequestsClick}>
                      <Inbox className="mr-2 h-4 w-4" />
                      <span className="flex-1">{t('My requests')}</span>
                      {pendingRequests > 0 && (
                        <span className={`ml-2 flex h-[17px] min-w-[17px] items-center justify-center rounded-md px-1 text-[11px] font-semibold ${badgeTone}`}>
                          {pendingRequests}
                        </span>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleProfileClick}>
                      <UserRound className="mr-2 h-4 w-4" />
                      <span>{t('My profile')}</span>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t('Log out')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {canImpersonate && (
        <ImpersonationDialog open={impersonationOpen} onOpenChange={setImpersonationOpen} />
      )}
    </nav>
  );
}

Header.propTypes = {
  username: PropTypes.string.isRequired,
  logo: PropTypes.string,
  serviceDeskName: PropTypes.string,
  onLogout: PropTypes.func.isRequired,
};

export { Header };